import React, { useMemo } from 'react'

import { useLayerApiUrl } from './ImportDataContext'
import {
  type BusinessIndustry,
  createCustomTransactions,
  type CustomAccount,
  type CustomTransactionParams,
  getTransferCustomTransactions,
  getTransferDepositoryCustomTransactions,
  validateCustomTransactions,
} from './importDummyDataHelpers'
import { ImportForm } from './ImportForm'
import { type ImportStepDef } from './useImportForm'

export interface ImportTransfersProps {
  demoName: string
  businessId: string
  industry: BusinessIndustry
  businessAccessToken: string
  defaultCustomAccount: CustomAccount
  defaultCustomDepositoryAccount: CustomAccount
}

const buildTransferSteps = (
  demoName: string,
  businessId: string,
  businessAccessToken: string,
  defaultAccountId: string,
  depositoryAccountId: string,
  layerApiUrl: string,
) => (transactions: CustomTransactionParams[]): ImportStepDef[] => [
  {
    label: 'Creating transactions in default account',
    run: async () => {
      await createCustomTransactions(
        businessId,
        businessAccessToken,
        defaultAccountId,
        transactions,
        layerApiUrl,
      )
    },
  },
  {
    label: 'Creating matching transactions in depository account',
    run: async () => {
      const depositoryTransactions = getTransferDepositoryCustomTransactions(
        transactions,
        demoName,
      )
      await createCustomTransactions(
        businessId,
        businessAccessToken,
        depositoryAccountId,
        depositoryTransactions,
        layerApiUrl,
      )
    },
  },
]

export const ImportTransfers = ({
  demoName,
  businessId,
  businessAccessToken,
  defaultCustomAccount,
  defaultCustomDepositoryAccount,
}: ImportTransfersProps) => {
  const layerApiUrl = useLayerApiUrl()
  const getSteps = useMemo(
    () => buildTransferSteps(
      demoName,
      businessId,
      businessAccessToken,
      defaultCustomAccount.id,
      defaultCustomDepositoryAccount.id,
      layerApiUrl,
    ),
    [demoName, businessId, businessAccessToken, defaultCustomAccount.id, defaultCustomDepositoryAccount.id, layerApiUrl],
  )

  return (
    <ImportForm
      title='Account Transfers'
      defaultCustomAccount={defaultCustomAccount}
      getInitialData={() => getTransferCustomTransactions(demoName)}
      validateData={validateCustomTransactions}
      getSteps={getSteps}
      dependencies={[demoName]}
    />
  )
}
